import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { api } from '../../utils/apiClient';
import './stripeCheckout.css';

/**
 * Props for the direct checkout process
 */
interface DirectCheckoutProps {
  tokenAmount: number;
  priceAmount: number;
  successUrl?: string;
  onError?: (error: string) => void;
  onStart?: () => void;
  userEmail?: string;
}

/**
 * Creates a Stripe checkout session and redirects to the Stripe hosted checkout page
 * This is a utility function rather than a component to simplify the checkout flow
 */
export const createStripeCheckout = async ({
  tokenAmount,
  priceAmount,
  successUrl = '/purchase-result',
  onError,
  onStart,
  userEmail
}: DirectCheckoutProps): Promise<void> => {
  try {
    if (onStart) onStart();
    
    // Validate that we have a user email
    if (!userEmail) {
      const errorMessage = 'User email is required to create a checkout session';
      console.error(errorMessage);
      if (onError) onError(errorMessage);
      return;
    }
    
    // Create a checkout session with the token amount and price
    const session = await api.wallet.createCheckoutSession(
      tokenAmount, 
      priceAmount,
      successUrl,
      userEmail
    );
    
    if (session.success && session.url) {
      // Directly redirect to Stripe checkout
      window.location.href = session.url;
    } else {
      const errorMessage = session.message || 'Failed to create checkout session';
      console.error('Session creation failed:', errorMessage);
      if (onError) onError(errorMessage);
    }
  } catch (error) {
    console.error('Error creating checkout session:', error);
    if (onError) onError('Error creating checkout session');
  }
};

/**
 * Legacy StripeCheckout component - maintained for backward compatibility
 * Automatically initiates a checkout and shows a loading state
 */
const StripeCheckout: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
  tokenAmount: number;
  priceAmount: number;
}> = ({ 
  onClose, 
  tokenAmount,
  priceAmount
}) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Start checkout on mount
  React.useEffect(() => {
    if (!user.email) {
      setError(t('payment.loginRequired', 'Please log in to purchase tokens'));
      setIsLoading(false);
      return;
    }
    
    createStripeCheckout({
      tokenAmount,
      priceAmount,
      userEmail: user.email,
      onError: (message) => {
        setError(message);
        setIsLoading(false);
      }
    });
  }, [user.email, tokenAmount, priceAmount, t]);
  
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
        </div>
        <span className="ms-3">{t('payment.redirecting', 'Redirecting to secure payment...')}</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <div className="mt-3">
          <button className="btn btn-secondary" onClick={onClose}>
            {t('common.close', 'Close')}
          </button>
        </div>
      </div>
    );
  }
  
  return null;
};

export default StripeCheckout; 